// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import {
  setHomeActive,
  setAlertsActive,
  setTicketActive,
  setCartActive,
  setProfileActive,
  setPaymentActive,
  resetState,
} from "@/features/uiStates/mobileFixedBottomMenuSlice";
import { getMisTicketsComprados } from "@/features/user/userThunks";
import { fetchUnreadCount } from "@/features/notificacion/notificacionThunks";

// React Utils
import { use, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Contexts
import { useCart } from "@/contexts/CartContext";
// Toast
import { useToast, ToastContainer } from "@/components/ui/Toast";
// Utils
import { scrollToTop, formatFechaHoraEvento } from "@/lib/utilsUI";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
// components
import Modal from "@/components/Modals/Modal";
import CartModal from "@/components/Modals/CartModal";
import PaymentModal from "@/components/Modals/PaymentCartModal";
import PaySuccessModal from "@/components/Modals/PaymentCartModal/PaySuccessModal";
import PayFailModal from "@/components/Modals/PaymentCartModal/PayFailModal";
import NotificationModal from "@/components/Modals/NotificationModal";
import TicketsModal from "@/components/Modals/TicketsModal";
// Assets
import EventImage1 from "@/assets/eventos/ViejasDeMierda.webp";
import EventImage2 from "@/assets/eventos/ClubAmandaDJCeleArrabal.webp";
import EventImage3 from "@/assets/eventos/ClubAmandaFullBailableYElectronic.webp";
import EventImage4 from "@/assets/eventos/CelebrarEnAlonso.webp";
import EventImage5 from "@/assets/eventos/Ferxxo.webp";
import EventImage6 from "@/assets/eventos/JorisVoorn.webp";
import AppWhoopLogo from "@/assets/AppWhoop_logo.webp";

export default function FixedMobileMenu() {
  // Redux Dispatch
  const dispatch = useDispatch();
  const {
    homeActive,
    alertsActive,
    ticketActive,
    cartActive,
    profileActive,
    paymentActive,
  } = useSelector((state) => state.mobileFixedBottomMenu);

  // Cart context
  const { carts } = useCart();

  // Toast
  const { toasts, removeToast } = useToast();

  // console.log("Mobile Menu State:", {
  //   homeActive: homeActive,
  //   alertsActive: alertsActive,
  //   ticketActive: ticketActive,
  //   cartActive: cartActive,
  //   profileActive: profileActive,
  // });

  // Redux state for authentication
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const unreadCount = useSelector(
    (state) => state.notificacion?.unreadCount || 0,
  );

  // Obtener la ruta actual
  const location = useLocation();

  // Fetch unread count on mount and location change
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUnreadCount());
    }
  }, [isAuthenticated, location.pathname, dispatch]);

  const [selectedCarritoUid, setSelectedCarritoUid] = useState(null);
  const [selectedKlapOrderId, setSelectedKlapOrderId] = useState(null); // Estado para klapOrderId
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isFailOpen, setIsFailOpen] = useState(false);

  // 🎫 Estados para tickets del modal
  const [ticketsForModal, setTicketsForModal] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [hasMoreTickets, setHasMoreTickets] = useState(false);

  // 🎫 Función para formatear tickets al formato del modal
  const formatTicketsForModal = (ticketsComprados) => {
    return ticketsComprados.flatMap((grupo) =>
      grupo.tickets.map((ticket) => ({
        id: ticket.id,
        uid: ticket.uid,
        type: "bought",
        title: grupo.evento.nombre,
        subtitle: `Ticket: ${ticket.tipoTicket?.nombre || "Sin tipo"}`,
        img: ticket.tipoTicketImg || ticket.eventoImg || grupo.evento.img,
        time: formatFechaHoraEvento(
          parseUTCDate(
            grupo.evento.fechaHoraInicio,
            grupo.evento.zonaHoraria || DEFAULT_TIMEZONE,
          )?.toJSDate(),
          grupo.evento.zonaHoraria || DEFAULT_TIMEZONE,
        ),
        seats: ticket.numeroTributario,
        eventoUid: grupo.evento.uid,
        vigente: ticket.vigente,
        fechaHoraInicio: grupo.evento.fechaHoraInicio,
      })),
    );
  };

  // 🎫 Función para cargar tickets próximos
  const loadTickets = async (page = 1, append = false) => {
    if (!isAuthenticated || ticketsLoading) return;

    setTicketsLoading(true);
    try {
      const now = new Date().toISOString();
      const response = await dispatch(
        getMisTicketsComprados({
          vigente: true,
          eventoFechaHoraInicioDesde: now,
          sortBy: "eventoFechaHoraInicio",
          sortOrder: "asc",
          page: page,
          limit: 10,
        }),
      ).unwrap();

      console.log("🎫 Mobile - Respuesta completa:", response);
      console.log("🎫 Mobile - Tickets array:", response.tickets);

      const formattedTickets = formatTicketsForModal(response.tickets || []);
      console.log("🎫 Mobile - Tickets formateados:", formattedTickets.length);

      if (append) {
        setTicketsForModal((prev) => [...prev, ...formattedTickets]);
      } else {
        setTicketsForModal(formattedTickets);
      }

      setTicketsPage(page);
      setHasMoreTickets(response.pagination?.hasNextPage || false);
    } catch (error) {
      console.error("❌ Mobile - Error loading tickets:", error);
    } finally {
      setTicketsLoading(false);
    }
  };

  // 🎫 Función para cargar más tickets
  const loadMoreTickets = () => {
    if (hasMoreTickets && !ticketsLoading) {
      loadTickets(ticketsPage + 1, true);
    }
  };

  // 🎫 Función para manejar apertura del modal de tickets
  const handleOpenTicketsModal = () => {
    console.log("🎫 Mobile - Abriendo modal de tickets...");

    dispatch(setTicketActive());

    // Siempre cargar tickets al abrir el modal
    if (!ticketsLoading && isAuthenticated) {
      console.log("🎫 Mobile - Iniciando carga de tickets...");
      // Reiniciar estado antes de cargar
      setTicketsForModal([]);
      setTicketsPage(1);
      setHasMoreTickets(false);
      loadTickets(1, false);
    }
  };

  // const verifyUserLogged = () => {
  //   // Lógica para verificar si el usuario está logueado
  //   // Por ejemplo, revisar un token en localStorage o una cookie
  //   const token = localStorage.getItem("authToken");
  //   return !!token; // Retorna true si hay token, false si no
  // }
  // useEffect(() => {
  //   setIsUserLogged(verifyUserLogged());
  // }, []);

  return (
    <nav className="fixed bottom-0 left-0 w-full bg-aw-dark rounded-t-3xl shadow-lg sm:hidden z-[90] border-t border-aw-bluegray">
      <div className="relative flex justify-evenly items-center h-16">
        {isAuthenticated ? (
          <>
            {/* Fondo con arco invertido */}
            <div className="absolute inset-0 flex justify-center items-start -top-[30%] w-fit mx-auto z-10">
              {/* Wallet (Centro con círculo especial) */}
              <button
                className={`relative flex flex-col items-center justify-center text-xs`}
                onClick={() => handleOpenTicketsModal()}
              >
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                    ticketActive
                      ? "bg-aw-green shadow-2xl shadow-aw-green"
                      : "bg-aw-green"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={"fa-solid fa-ticket"}
                    size="lg"
                    className="text-black -rotate-z-45"
                  />
                </div>
              </button>
            </div>
            <TicketsModal
              isOpen={ticketActive}
              onClose={() => dispatch(resetState())}
              tickets={ticketsForModal}
              isLoading={ticketsLoading}
              hasMore={hasMoreTickets}
              onLoadMore={loadMoreTickets}
            />
            {/* Home */}
            <Link
              to={"/"}
              onClick={() => {
                scrollToTop();
                dispatch(setHomeActive());
              }}
              className={`flex flex-col items-center text-xs transition duration-300 rounded-full py-2 px-3 ${
                location.pathname === "/" ? "bg-aw-green" : "text-gray-300"
              }`}
            >
              <FontAwesomeIcon
                icon={"fa-solid fa-home"}
                size="lg"
                className={`mb-1 ${
                  location.pathname === "/" ? "text-aw-dark" : "text-gray-300"
                }`}
              />
              Home
            </Link>

            {/* Carrito */}
            <button
              onClick={() => dispatch(setCartActive())}
              className={`flex flex-col items-center text-xs transition-colors ${
                cartActive
                  ? "text-aw-green shadow-2xl shadow-aw-green"
                  : "text-gray-300"
              }`}
            >
              <FontAwesomeIcon
                icon={"fa-solid fa-cart-shopping"}
                size="lg"
                className={`mb-1 ${cartActive ? "text-aw-green" : "text-gray-300"}`}
              />
              Carrito
            </button>
            <CartModal
              isOpen={cartActive}
              onClose={() => dispatch(resetState())}
              carts={carts}
              onOpenPayment={(carritoUid, klapOrderId) => {
                setSelectedCarritoUid(carritoUid);
                setSelectedKlapOrderId(klapOrderId);
              }}
            />

            <div className="w-20 h-20 rounded-full -translate-y-1 shadow-inner z-0" />

            {/* Actividad Reciente */}
            <button
              onClick={() => dispatch(setAlertsActive())}
              className={`relative flex flex-col items-center text-xs transition-colors ${
                alertsActive ? "text-aw-green" : "text-gray-300"
              }`}
            >
              <FontAwesomeIcon
                icon={"fa-solid fa-bell"}
                size="lg"
                className={`mb-1 ${
                  alertsActive ? "text-aw-green" : "text-gray-300"
                }`}
              />
              Actividad
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </div>
              )}
            </button>
            <NotificationModal
              isOpen={alertsActive}
              onClose={() => dispatch(resetState())}
            />

            {/* Perfil */}
            <Link
              to={`/perfil/${user.uid}`}
              onClick={() => {
                scrollToTop();
                dispatch(setProfileActive());
              }}
              className={`flex flex-col items-center text-xs transition duration-300 rounded-full py-2 px-3 ${
                location.pathname === `/perfil/${user.uid}`
                  ? "bg-aw-green"
                  : "text-gray-300"
              }`}
            >
              <FontAwesomeIcon
                icon={"fa-solid fa-user"}
                size="lg"
                className={`mb-1 ${
                  location.pathname === `/perfil/${user.uid}`
                    ? "text-aw-dark "
                    : "text-gray-300"
                }`}
              />
              Perfil
            </Link>
          </>
        ) : (
          <>
            {/* Logo central elevado */}
            <div className="absolute inset-0 flex justify-center items-start -top-[30%] w-fit mx-auto z-10">
              <Link to="/">
                <img
                  src={AppWhoopLogo}
                  alt="AppWhoop"
                  className="w-20 h-20 object-contain -mt-4"
                />
              </Link>
            </div>

            {/* Registrarse */}
            <Link
              to="/signup"
              state={location.pathname.startsWith("/evento/") ? { from: location.pathname } : undefined}
              className="flex flex-col items-center text-xs text-aw-green/85"
            >
              <FontAwesomeIcon
                icon={"fa-solid fa-user-plus"}
                size="lg"
                className="mb-1 text-aw-green"
              />
              Registrarse
            </Link>

            {/* Spacer para el círculo elevado */}
            <div className="w-20 h-20 rounded-full -translate-y-1 shadow-inner z-0" />

            {/* Iniciar Sesión */}
            <Link
              to="/login"
              state={location.pathname.startsWith("/evento/") ? { from: location.pathname } : undefined}
              className="flex flex-col items-center text-xs text-aw-green/85"
            >
              <FontAwesomeIcon
                icon={"fa-solid fa-right-to-bracket"}
                size="lg"
                className="mb-1 text-aw-green"
              />
              Iniciar Sesión
            </Link>
          </>
        )}

        {/* Toast Container */}
        <ToastContainer toasts={toasts} onRemove={removeToast} />
      </div>
    </nav>
  );
}
